import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@next/core-lib/i18n';
import {
  EMPTY,
  Subject,
  catchError,
  concatMap,
  debounceTime,
  defer,
  filter,
  finalize,
  map,
  tap,
} from 'rxjs';

import {
  DairyYieldScanActionPlan,
  DairyYieldScanImprovement,
  UpdateDairyYieldScanActionPlanRequest,
} from '../../models/dairy-yield-scan.models';
import { DairyYieldScanActionPlanDraftStore } from '../../services/dairy-yield-scan-action-plan-draft.store';
import { DairyYieldScanService } from '../../services/dairy-yield-scan.service';

type PriorityControlName = 'priority1' | 'priority2' | 'priority3';
type OtherPriorityControlName = 'otherPriority1' | 'otherPriority2' | 'otherPriority3';
type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

const AUTOSAVE_DEBOUNCE_MILLISECONDS = 800;
const OTHER_PRIORITY = 'other';
const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const localToday = (): Date => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

const parseLocalDate = (value: string | null | undefined): Date | null => {
  const match = value?.match(LOCAL_DATE_PATTERN);
  if (!match) {
    return null;
  }

  const [, year, month, day] = match.map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null;
};

const formatLocalDate = (value: Date | null): string | null => {
  if (!value || Number.isNaN(value.getTime())) {
    return null;
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const otherPriorityValidator = (control: AbstractControl): ValidationErrors | null => {
  const values = control.getRawValue() as Record<string, string | null>;
  return ['1', '2', '3'].some(
    (index) =>
      values[`priority${index}`] === OTHER_PRIORITY && !values[`otherPriority${index}`]?.trim(),
  )
    ? { otherPriorityRequired: true }
    : null;
};

@Component({
  selector: 'app-dairy-yield-scan-actions',
  templateUrl: './dairy-yield-scan-actions.component.html',
  styleUrls: ['./dairy-yield-scan-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class DairyYieldScanActionsComponent implements OnChanges {
  @Input({ required: true }) scanId = '';
  @Input({ required: true }) improvements: readonly DairyYieldScanImprovement[] = [];
  @Input({ required: true }) actionPlan: DairyYieldScanActionPlan | null = null;
  @Input() currentMilkYield: number | null = null;
  @Input() targetMilkYield: number | null = null;

  readonly saveState = signal<SaveState>('idle');
  readonly minEvaluationDate = localToday();
  readonly priorityFields: ReadonlyArray<{
    controlName: PriorityControlName;
    otherControlName: OtherPriorityControlName;
    labelKey: string;
    required: boolean;
  }> = [
    {
      controlName: 'priority1',
      otherControlName: 'otherPriority1',
      labelKey: 'dairyYieldScan.summary.actions.priority1',
      required: true,
    },
    {
      controlName: 'priority2',
      otherControlName: 'otherPriority2',
      labelKey: 'dairyYieldScan.summary.actions.priority2',
      required: false,
    },
    {
      controlName: 'priority3',
      otherControlName: 'otherPriority3',
      labelKey: 'dairyYieldScan.summary.actions.priority3',
      required: false,
    },
  ];
  readonly form = new FormGroup(
    {
      priority1: new FormControl<string | null>(null, Validators.required),
      priority2: new FormControl<string | null>(null),
      priority3: new FormControl<string | null>(null),
      otherPriority1: new FormControl('', {
        nonNullable: true,
        validators: Validators.maxLength(200),
      }),
      otherPriority2: new FormControl('', {
        nonNullable: true,
        validators: Validators.maxLength(200),
      }),
      otherPriority3: new FormControl('', {
        nonNullable: true,
        validators: Validators.maxLength(200),
      }),
      farmerAction: new FormControl('', {
        nonNullable: true,
        validators: Validators.maxLength(1000),
      }),
      advisorAction: new FormControl('', {
        nonNullable: true,
        validators: Validators.maxLength(1000),
      }),
      evaluationDate: new FormControl<Date | null>(null),
    },
    { validators: otherPriorityValidator },
  );
  readonly formVersion = signal(0);
  readonly hasMilkYieldData = computed(() => {
    this.formVersion();
    return (
      typeof this.currentMilkYield === 'number' &&
      Number.isFinite(this.currentMilkYield) &&
      typeof this.targetMilkYield === 'number' &&
      Number.isFinite(this.targetMilkYield)
    );
  });
  readonly selectedActionPotential = computed(() => {
    this.formVersion();
    return this.priorityFields
      .map(({ controlName }) => this.form.controls[controlName].value)
      .filter((value): value is string => Boolean(value && value !== OTHER_PRIORITY))
      .reduce(
        (total, questionKey) =>
          total +
          (this.improvements.find((improvement) => improvement.questionKey === questionKey)
            ?.potential ?? 0),
        0,
      );
  });
  readonly improvedMilkProduction = computed(() =>
    this.currentMilkYield === null ? null : this.currentMilkYield + this.selectedActionPotential(),
  );
  readonly remainingToTarget = computed(() => {
    const improvedProduction = this.improvedMilkProduction();
    return improvedProduction === null || this.targetMilkYield === null
      ? null
      : Math.max(0, this.targetMilkYield - improvedProduction);
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly dairyYieldScanService = inject(DairyYieldScanService);
  private readonly actionPlanDraftStore = inject(DairyYieldScanActionPlanDraftStore);
  private readonly saveRequests = new Subject<UpdateDairyYieldScanActionPlanRequest>();
  private readonly pendingSaveRequests: UpdateDairyYieldScanActionPlanRequest[] = [];
  private persistedEvaluationDate: string | null = null;
  private lastPersistedRequest: UpdateDairyYieldScanActionPlanRequest | null = null;
  private lastFailedRequest: UpdateDairyYieldScanActionPlanRequest | null = null;

  constructor() {
    this.saveRequests
      .pipe(
        concatMap((request) =>
          defer(() =>
            this.dairyYieldScanService.updateActionPlan(this.scanId, request).pipe(
              tap(() => {
                this.lastPersistedRequest = request;
                this.persistedEvaluationDate = request.evaluationDate;
                this.lastFailedRequest = null;
              }),
              catchError(() => {
                this.lastFailedRequest = request;
                return EMPTY;
              }),
              finalize(() => {
                const requestIndex = this.pendingSaveRequests.indexOf(request);
                if (requestIndex >= 0) {
                  this.pendingSaveRequests.splice(requestIndex, 1);
                }
                this.updateSaveStateAfterEdit();
              }),
            ),
          ),
        ),
      )
      .subscribe();

    this.form.valueChanges
      .pipe(
        tap(() => {
          this.formVersion.update((version) => version + 1);
          this.actionPlanDraftStore.setActionPlan(this.createRequest());
          this.updateSaveStateAfterEdit();
        }),
        debounceTime(AUTOSAVE_DEBOUNCE_MILLISECONDS),
        filter(() => this.form.valid),
        map(() => this.createRequest()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((request) => this.queueSave(request));

    this.destroyRef.onDestroy(() => {
      if (this.form.valid && ['dirty', 'error'].includes(this.saveState())) {
        this.queueSave(this.createRequest());
      }
      this.saveRequests.complete();
    });
  }

  readonly evaluationDateFilter = (date: Date | null): boolean =>
    date === null ||
    date >= this.minEvaluationDate ||
    formatLocalDate(date) === this.persistedEvaluationDate;

  ngOnChanges() {
    this.persistedEvaluationDate = this.actionPlan?.evaluationDate ?? null;
    const availableQuestionKeys = new Set(
      this.improvements.map((improvement) => improvement.questionKey),
    );
    const savedPriorities = this.actionPlan?.priorities ?? [];
    const [priority1, priority2, priority3] = this.priorityFields.map((_, index) =>
      this.priorityValues(savedPriorities[index], availableQuestionKeys),
    );
    this.form.setValue(
      {
        priority1: priority1.value ?? this.improvements[0]?.questionKey ?? null,
        priority2: priority2.value,
        priority3: priority3.value,
        otherPriority1: priority1.otherValue,
        otherPriority2: priority2.otherValue,
        otherPriority3: priority3.otherValue,
        farmerAction: this.actionPlan?.farmerAction ?? '',
        advisorAction: this.actionPlan?.advisorAction ?? '',
        evaluationDate: parseLocalDate(this.actionPlan?.evaluationDate),
      },
      { emitEvent: false },
    );
    this.formVersion.update((version) => version + 1);
    this.lastPersistedRequest = this.actionPlan ? this.createRequest() : null;
    this.lastFailedRequest = null;
    this.saveState.set('idle');
    this.actionPlanDraftStore.setActionPlan(this.createRequest());
  }

  isPriorityUnavailable(questionKey: string, currentControl: PriorityControlName): boolean {
    return (['priority1', 'priority2', 'priority3'] as const).some(
      (controlName) =>
        controlName !== currentControl && this.form.controls[controlName].value === questionKey,
    );
  }

  isOtherPriority(controlName: PriorityControlName): boolean {
    return this.form.controls[controlName].value === OTHER_PRIORITY;
  }

  retrySave() {
    if (this.form.valid) {
      this.queueSave(this.createRequest());
    }
  }

  private createRequest(): UpdateDairyYieldScanActionPlanRequest {
    const values = this.form.getRawValue();
    return {
      priorities: this.priorityFields
        .map(({ controlName, otherControlName }) =>
          values[controlName] === OTHER_PRIORITY
            ? values[otherControlName].trim() || null
            : values[controlName],
        )
        .filter((priority): priority is string => priority !== null),
      farmerAction: values.farmerAction.trim() || null,
      advisorAction: values.advisorAction.trim() || null,
      evaluationDate: formatLocalDate(values.evaluationDate),
    };
  }

  private priorityValues(
    priority: string | undefined,
    availableQuestionKeys: Set<string>,
  ): { value: string | null; otherValue: string } {
    if (!priority) {
      return { value: null, otherValue: '' };
    }

    if (availableQuestionKeys.has(priority)) {
      return { value: priority, otherValue: '' };
    }

    return { value: OTHER_PRIORITY, otherValue: priority };
  }

  private updateSaveStateAfterEdit() {
    const request = this.createRequest();
    const latestPendingRequest = this.pendingSaveRequests.at(-1);
    if (latestPendingRequest) {
      this.saveState.set(this.requestsEqual(request, latestPendingRequest) ? 'saving' : 'dirty');
    } else if (
      this.lastPersistedRequest &&
      this.requestsEqual(request, this.lastPersistedRequest)
    ) {
      this.saveState.set('saved');
    } else if (this.lastFailedRequest && this.requestsEqual(request, this.lastFailedRequest)) {
      this.saveState.set('error');
    } else {
      this.saveState.set('dirty');
    }
  }

  private queueSave(request: UpdateDairyYieldScanActionPlanRequest) {
    const latestPendingRequest = this.pendingSaveRequests.at(-1);
    if (
      (latestPendingRequest && this.requestsEqual(request, latestPendingRequest)) ||
      (!latestPendingRequest &&
        this.lastPersistedRequest &&
        this.requestsEqual(request, this.lastPersistedRequest))
    ) {
      this.updateSaveStateAfterEdit();
      return;
    }

    this.pendingSaveRequests.push(request);
    this.saveRequests.next(request);
    this.updateSaveStateAfterEdit();
  }

  private requestsEqual(
    first: UpdateDairyYieldScanActionPlanRequest,
    second: UpdateDairyYieldScanActionPlanRequest,
  ): boolean {
    return (
      first.farmerAction === second.farmerAction &&
      first.advisorAction === second.advisorAction &&
      first.evaluationDate === second.evaluationDate &&
      first.priorities.length === second.priorities.length &&
      first.priorities.every((priority, index) => priority === second.priorities[index])
    );
  }
}
